import { FC, MouseEvent, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/ReduxHooks";
import EditInputField from "../../UI/edit-input-field/EditInputField";
import { useNavigate } from "react-router-dom";
import { IUserImzoBody } from "../../../redux/types/IdentificationTypes";
import {
  resetStates,
  updateImzoUser,
} from "../../../redux/requests/IdentificationRequests";
import { toast } from "react-toastify";
import { toastError, toastSuccess } from "../../../utils/toast";
import PhoneVerification from "../../UI/phone-verification/PhoneVerification";
import useOutsideAlerter from "../../../hooks/UseOutSide";

interface IEditPageProps {
  backPassportPhoto: string | null;
  frontPassportPhoto: string | null;
}

const EditPage: FC<IEditPageProps> = ({
  backPassportPhoto,
  frontPassportPhoto,
}) => {
  const { frontPassportData, backPassportData, compareFacesResult } =
    useAppSelector((state) => state.user);
  const error = useAppSelector((state) => state.user.error);
  const isLoading = useAppSelector((state) => state.user.isLoading);
  const operator = useAppSelector((state) => state.user.operator);
  const clientPhoneNumber = useAppSelector(
    (state) => state.user.clientPhoneNumber,
  );

  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const [phone, setPhone] = useState<string>("992");
  const [address, setAddress] = useState<string>("");
  const [issueBy, setIssueBy] = useState<string>("");
  const [issueByEn, setIssueByEn] = useState<string>("");
  const [surname, setSurname] = useState("");
  const [name, setName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [surnameEn, setSurnameEn] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [middleNameEn, setMiddleNameEn] = useState("");
  const [inn, setInn] = useState("");

  const { isShow, setIsShow, modalRef } = useOutsideAlerter(false);

  useEffect(() => {
    if (
      !frontPassportData ||
      !backPassportData ||
      !frontPassportPhoto ||
      !backPassportPhoto
    ) {
      navigate("/identification");
      return;
    }
    setAddress(backPassportData?.["Address-Tajik (Cyrillic)"]);
    setIssueBy(backPassportData["Authority-Tajik (Cyrillic)"]);
    setIssueByEn(backPassportData.Authority);
    setName(frontPassportData["Given Names-Tajik (Cyrillic)"]);
    setNameEn(frontPassportData["Given Names"]);
    setSurname(frontPassportData["Surname-Tajik (Cyrillic)"]);
    setSurnameEn(frontPassportData.Surname);
    setMiddleName(frontPassportData["Fathers Name-Tajik (Cyrillic)"]);
    setMiddleNameEn(frontPassportData["Fathers Name"]);
    setInn(backPassportData["Reference Number"]);

    if (operator?.id === 91) return;

    if (!compareFacesResult) {
      navigate("/identification");
      return;
    }
  }, [
    frontPassportData,
    backPassportData,
    compareFacesResult,
    frontPassportPhoto,
    backPassportPhoto,
  ]);

  useEffect(() => {
    if (!clientPhoneNumber) return;
    setIsShow(false);
  }, [clientPhoneNumber]);

  useEffect(() => {
    if (!error) return;
    toastError(error);
  }, [error]);

  const updateUserClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!address || !issueBy || !issueByEn || !phone) {
      toastError("Необходимо заполнить все поля формы");
      return;
    }

    if (!clientPhoneNumber) {
      toastError("Подтвердите номер телефона клиента!");
      return;
    }

    if (
      !backPassportData ||
      !frontPassportData ||
      !frontPassportPhoto ||
      !backPassportPhoto
    )
      return;

    if (!operator) return;

    const data: IUserImzoBody = {
      address: address,
      blood_group: backPassportData["Blood Group"],
      date_of_birth: frontPassportData["Date of Birth"],
      pass_number: frontPassportData["Document Number"],
      expiry_date: frontPassportData["Date of Expiry"],
      first_name: name,
      first_name_en: nameEn,
      inn: inn,
      issue_date: frontPassportData["Date of Issue"],
      issuin_agency: issueBy,
      issuin_agency_en: issueByEn,
      last_name: surname,
      last_name_en: surnameEn,
      middle_name: middleName,
      middle_name_en: middleNameEn,
      national_id: frontPassportData["Personal Number"],
      nationality_code: frontPassportData["Nationality Code-Tajik (Cyrillic)"],
      nationality_code_en: frontPassportData["Nationality Code"],
      phone_number: clientPhoneNumber,
      gender: frontPassportData.Sex,
      front_passport_base64: frontPassportPhoto,
      back_passport_base64: backPassportPhoto,
      user_id: Number(operator.id),
    };
    const imzoUpdateUserResult = await dispatch(updateImzoUser(data));
    if (!imzoUpdateUserResult) return;
    await dispatch(resetStates());
    toastSuccess("Данные обновлены");
    navigate("/inn-check");
  };

  const verificationPhoneModalClick = (
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    setIsShow(true);
  };

  return (
    <div className="mb-4">
      <h1 className="font-bold text-2xl mb-10 text-center">
        Проверьте данные ещё раз
      </h1>
      <form>
        <div className="grid gap-6 mb-6 md:grid-cols-3">
          <EditInputField
            data={{
              id: 1,
              type: "text",
              value: surname,
              labelText: "Фамилия",
            }}
            readonly={false}
            setValue={setSurname}
          />
          <EditInputField
            data={{
              id: 2,
              type: "text",
              value: name,
              labelText: "Имя",
            }}
            readonly={false}
            setValue={setName}
          />
          <EditInputField
            data={{
              id: 3,
              type: "text",
              value: middleName,
              labelText: "Отчество",
            }}
            readonly={false}
            setValue={setMiddleName}
          />
        </div>

        <div className="grid gap-6 mb-6 md:grid-cols-3">
          <EditInputField
            data={{
              id: 17,
              type: "text",
              value: surnameEn,
              labelText: "Фамилия на английском",
            }}
            readonly={false}
            setValue={setSurnameEn}
          />
          <EditInputField
            data={{
              id: 18,
              type: "text",
              value: nameEn,
              labelText: "Имя на английском",
            }}
            readonly={false}
            setValue={setNameEn}
          />
          <EditInputField
            data={{
              id: 19,
              type: "text",
              value: middleNameEn,
              labelText: "Отчество на английском",
            }}
            readonly={false}
            setValue={setMiddleNameEn}
          />
        </div>

        <div className="grid gap-6 md:grid-cols-4 mb-6">
          <EditInputField
            data={{
              id: 4,
              type: "text",
              value: frontPassportData?.["Sex-Tajik (Cyrillic)"],
              labelText: "Пол",
            }}
            readonly={true}
          />
          <EditInputField
            data={{
              id: 5,
              type: "text",
              value: frontPassportData?.["Nationality Code-Tajik (Cyrillic)"],
              labelText: "Национальность",
            }}
            readonly={true}
          />
          <EditInputField
            data={{
              id: 6,
              type: "text",
              value: frontPassportData?.["Date of Birth"],
              labelText: "Дата рождения",
            }}
            readonly={true}
          />
          <EditInputField
            data={{
              id: 7,
              type: "text",
              value: frontPassportData?.["Place of Birth-Tajik (Cyrillic)"],
              labelText: "Место рождения",
            }}
            readonly={true}
          />
        </div>

        <div className="grid gap-6 md:grid-cols-4 mb-6">
          <EditInputField
            data={{
              id: 8,
              type: "text",
              value: frontPassportData?.["Document Number"],
              labelText: "Номер паспорта",
            }}
            readonly={true}
          />
          <EditInputField
            data={{
              id: 9,
              type: "text",
              value: frontPassportData?.["Date of Issue"],
              labelText: "Дата выдачи",
            }}
            readonly={true}
          />
          <EditInputField
            data={{
              id: 10,
              type: "text",
              value: frontPassportData?.["Date of Expiry"],
              labelText: "Дата окончания",
            }}
            readonly={true}
          />
          <EditInputField
            data={{
              id: 11,
              type: "text",
              value: frontPassportData?.["Personal Number"],
              labelText: "Единый национальный номер",
            }}
            readonly={true}
          />
        </div>

        <div className="grid gap-6 md:grid-cols-3 mb-6">
          <EditInputField
            data={{
              id: 12,
              type: "text",
              value: backPassportData?.["Marital Status-Tajik (Cyrillic)"],
              labelText: "Семейный статус",
            }}
            readonly={true}
          />
          <EditInputField
            data={{
              id: 13,
              type: "text",
              value: backPassportData?.["Blood Group"],
              labelText: "Группа крови",
            }}
            readonly={true}
          />

          {operator?.id === 91 ? (
            <EditInputField
              data={{
                id: 14,
                type: "text",
                value: inn,
                labelText: "ИНН",
              }}
              setValue={setInn}
              readonly={false}
            />
          ) : (
            <EditInputField
              data={{
                id: 14,
                type: "text",
                value: inn,
                labelText: "ИНН",
              }}
              readonly={true}
            />
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2 mb-6 items-end">
          <EditInputField
            data={{
              id: 17,
              type: "text",
              value: clientPhoneNumber,
              labelText: "Номер телефона",
            }}
            readonly={true}
            maxLength={12}
          />
          {clientPhoneNumber && (
            <svg
              width="30px"
              height="30px"
              viewBox="0 0 1024 1024"
              className="icon"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M512 512m-448 0a448 448 0 1 0 896 0 448 448 0 1 0-896 0Z"
                fill="#4CAF50"
              />
              <path
                d="M738.133333 311.466667L448 601.6l-119.466667-119.466667-59.733333 59.733334 179.2 179.2 349.866667-349.866667z"
                fill="#CCFF90"
              />
            </svg>
          )}
          <button
            className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center"
            onClick={verificationPhoneModalClick}
          >
            Изменить
          </button>
        </div>

        <div className="grid gap-6 md:grid-cols-1 mb-6">
          <EditInputField
            data={{
              id: 15,
              type: "text",
              value: issueBy,
              labelText: "Кем выдан",
            }}
            readonly={false}
            setValue={setIssueBy}
          />
        </div>

        <div className="grid gap-6 md:grid-cols-1 mb-6">
          <EditInputField
            data={{
              id: 20,
              type: "text",
              value: issueByEn,
              labelText: "Кем выдан на английском",
            }}
            readonly={false}
            setValue={setIssueByEn}
          />
        </div>

        <div className="grid gap-6 md:grid-cols-1 mb-6">
          <EditInputField
            data={{
              id: 16,
              type: "text",
              value: address,
              labelText: "Адрес",
            }}
            readonly={false}
            setValue={setAddress}
          />
        </div>

        {false ? (
          <button
            disabled
            type="button"
            className="py-2.5 px-5 me-2 text-sm font-medium text-gray-900 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:outline-none focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700 flex justify-center items-center w-full"
          >
            <svg
              aria-hidden="true"
              role="status"
              className="inline w-4 h-4 me-3 text-gray-200 animate-spin dark:text-gray-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="#1C64F2"
              />
            </svg>
            Проверка данных...
          </button>
        ) : (
          <button
            className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center"
            onClick={updateUserClick}
          >
            Продолжить
          </button>
        )}
      </form>
      <PhoneVerification
        isActive={isShow}
        setIsActive={setIsShow}
        ref={modalRef}
      />
    </div>
  );
};

export default EditPage;
